import { useEffect, useRef, useState } from 'react';

export interface EJOTProductOption {
  value: string;
  name: string;
  meta?: string;
  imageSrc?: string;
}

export function EJOTProductSelect({ label, options, value, placeholder, onChange }: {
  label: string;
  options: EJOTProductOption[];
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const selected = options.find((option) => option.value === value);

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', key);
    };
  }, [open]);

  return (
    <div className="field ejot-product-select" ref={rootRef}>
      <span className="field-label">{label}</span>
      <button
        type="button"
        className="ejot-product-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <ProductThumb option={selected} />
        <span className="ejot-product-copy">
          <strong>{selected?.name ?? placeholder}</strong>
          {selected?.meta && <span>{selected.meta}</span>}
        </span>
        <span className="ejot-product-chevron" aria-hidden>›</span>
      </button>

      {open && options.length > 0 && (
        <ul className="ejot-product-menu" role="listbox" aria-label={label}>
          {options.map((option) => (
            <li key={option.value} role="option" aria-selected={option.value === value}>
              <button
                type="button"
                className={option.value === value ? 'ejot-product-option is-selected' : 'ejot-product-option'}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
              >
                <ProductThumb option={option} />
                <span className="ejot-product-copy">
                  <strong>{option.name}</strong>
                  {option.meta && <span>{option.meta}</span>}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ProductThumb({ option }: { option?: EJOTProductOption }) {
  const [failed, setFailed] = useState(false);
  const src = option?.imageSrc;
  useEffect(() => setFailed(false), [src]);
  if (!src || failed) {
    return <span className="ejot-product-thumb is-missing" aria-hidden>Image unavailable</span>;
  }
  return (
    <span className="ejot-product-thumb">
      <img src={src} alt="" onError={() => setFailed(true)} />
    </span>
  );
}
